import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { useAuth } from '../customHooks/useAuth'
import axios from '../utils/axios'

interface FavoritesContextType {
  favorites: string[]
  toggleFavorite: (appName: string) => void
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined)

interface FavoritesProviderProps {
  children: ReactNode
}

const FAV_API = process.env.REACT_APP_FAVORITE_API as string
const REMOVE_FAVORITE_API = process.env.REACT_APP_REMOVE_FAVORITE_API as string

export type FavResponse = {
  data: {
    userFavoriteAppList: string[]
  }
}

export const FavoritesProvider: React.FC<FavoritesProviderProps> = ({ children }) => {
  const { user, logoutInProgress } = useAuth()
  const [favorites, setFavorites] = useState<string[]>([])

  useEffect(() => {
    // Initial fetch from server
    const fetchFavorites = async () => {
      try {
        const config = {
          method: 'GET',
          url: `${FAV_API}/list`,
          headers: {
            'x-user': user?.email,
            'x-access-token': user?.tokens?.accessToken?.token,
            'x-source': user?.isSSO ? 'sso' : null,
          },
        }
        const response = await axios.request<FavResponse>(config)
        setFavorites(response?.data?.data?.userFavoriteAppList)
      } catch {
        setFavorites([])
      }
    }

    // Don't make API calls if logout is in progress
    if (logoutInProgress) {
      setFavorites([])
      return
    }

    // Only fetch favorites if user is authenticated and has valid tokens
    if (user?.tokens?.accessToken?.token && user.authenticated) {
      fetchFavorites()
    } else if (!user || !user.authenticated) {
      // Clear favorites when user is logged out or not authenticated
      setFavorites([])
    }
  }, [user, logoutInProgress])

  const toggleFavorite = async (appName: string) => {
    const isFavorite = favorites?.includes(appName)
    const updatedFavorites = isFavorite
      ? favorites?.filter((id) => id !== appName)
      : [...favorites, appName]

    setFavorites(updatedFavorites)

    try {
      const config = {
        url: isFavorite ? REMOVE_FAVORITE_API : FAV_API,
        method: isFavorite ? 'DELETE' : 'POST',
        headers: {
          'x-user': user?.email,
          'x-access-token': user?.tokens?.accessToken?.token,
          'x-source': user?.isSSO ? 'sso' : null,
        },
        data: {
          appSysId: appName,
        },
      }
      await axios.request(config)
    } catch {
      setFavorites(favorites)
    }
  }

  return (
    <FavoritesContext.Provider value={{ favorites, toggleFavorite }}>
      {children}
    </FavoritesContext.Provider>
  )
}

// Custom hook
export const useFavorites = (): FavoritesContextType => {
  const context = useContext(FavoritesContext)
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider')
  }
  return context
}
